import React, { forwardRef, useState, useImperativeHandle, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Stack,
  Button,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormGroup,
  CircularProgress,
  Typography,
  Link
} from "@mui/material";

import { AppContext } from "../../context/AppProvider";
import { Email } from "../../components/Email";
import { Text } from "../../components/Text";

const stripe = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const query = new URLSearchParams(window.location.search);
const stripeRedirectUrl = query.get("checkout_url") || `${process.env.REACT_APP_HOST_URL}/summary`;
console.log(`Stripe redirect url: ${stripeRedirectUrl}`);

const Payment = forwardRef((props, ref) => {
  const {configuration, setIsProcessing, setError} = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);

  const elements = useElements();
  const stripe = useStripe();

  const makePayment = async () => {
    setIsProcessing(true);

    const { id, firstName, lastName, email } = configuration;

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${stripeRedirectUrl}?customer=${id}`,
        payment_method_data: {
          billing_details: {
            name: `${firstName} ${lastName}`,
            email
          },
        }
      },
    });

    if (error) {
      setIsProcessing(false);
      setError(error.message);
    }
  }

  useImperativeHandle(ref, () => ({
    makePayment
  }));

  return (
    <Stack direction="column" spacing={2}>
      {isLoading && (
        <Stack justifyContent="center" alignItems="center" style={{ minHeight: "200px" }}>
          <CircularProgress color="inherit" />
        </Stack>
      )}
      <PaymentElement
        onReady={() => setIsLoading(false)}
        onChange={(e) => props.onChange && props.onChange(e.complete)}
      />
    </Stack>
  )
});

export const CheckoutForm = (props) => {
  const {
    configuration,
    payment,
    setFirstName,
    setLastName,
    setCompany,
    setEmail,
    validateEmail,
    validateOTP,
    updateSubscription,
  } = useContext(AppContext);

  const { firstName, lastName, company, email } = configuration;

  const [providedPayment, setProvidedPayment] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedService, setAcceptedService] = useState(false);

  

  const navigate = useNavigate();

  const paymentRef = React.useRef();

  const showTermsAndConditions = (e) => {
    e.preventDefault();
    window.open(process.env.REACT_APP_TERMS_URL, "_blank");
  }

  const goToConfiguration = () => {
    navigate(configuration.promotion ? `/configure?p=1` : `/configure`);
  }

  const makePayment = async () => {
    await updateSubscription();
    await paymentRef.current.makePayment();
  }

  return (
    <Stack direction="column" spacing={3} sx={{width:"100%"}}>
          <Text label="First Name *" value={firstName} onChange={setFirstName} />
          <Text label="Last Name *" value={lastName} onChange={setLastName} />
          <Text label="Company *" value={company} onChange={setCompany} />

        <Email
          value={email}
          onValidateEmail={(email) => validateEmail(email)}
          onValidateCode={(code) => validateOTP(code)}
          onChange={(email) => setEmail(email)} />

        <InputLabel sx={{fontWeight: 'bold', color: '#000'}}>Payment</InputLabel>
        {payment.secret ? (
          <Elements
            stripe={stripe}
            options={{
              clientSecret: payment.secret,
            }}
          >
            <Payment ref={paymentRef} onChange={setProvidedPayment} />

            <Stack alignItems="flex-end">
              <a href="https://stripe.com" target="_blank" rel="noreferrer">
                <img width="100px" src="/stripe-badge.svg" alt="Payments powered by Stripe" />
              </a>
            </Stack>
          </Elements>
        ) : (
          <Typography variant="body2">Please fill all invoice details before making the payment.</Typography>
        )}

      <FormGroup>
        <FormControlLabel
          control={<Checkbox size="small" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />}
          label={<Typography variant="body2">I accept Kasm <Link onClick={showTermsAndConditions}>EULA & Terms of Service</Link></Typography>} />
      </FormGroup>
      <FormGroup style={{ marginTop: "0"}}>
        <FormControlLabel
          control={<Checkbox size="small" checked={acceptedService} onChange={(e) => setAcceptedService(e.target.checked)} />}
          label={<Typography variant="body2">I understand the service is self supported and doesn't include the ability to submit service requests.</Typography>} />
      </FormGroup>

      {acceptedTerms && acceptedService && !providedPayment &&
        <div style={{ color: '#c00', fontWeight: 'bold'}}>
          The payment form is not complete. Ensure all fields are correctly filled in (including postal/zip code).
        </div>
        }

      <Stack direction="row" justifyContent="center" spacing={10}>
        <Button
          variant="outlined"
          color="primary"
          onClick={goToConfiguration}>Back</Button>
        <Button
          disabled={!firstName || !lastName || !company || !email || !providedPayment || !acceptedTerms || !acceptedService}
          variant="contained"
          color="primary"
          onClick={makePayment}>Complete Checkout</Button>
      </Stack>
    </Stack >
  )
}